// Right-hand slide-over shell used by every detail view on this dashboard.
//
// Hand-rolled rather than @radix-ui/react-dialog: the repo doesn't depend on it
// and this page shouldn't be the thing that adds it. That means we own the bits
// Radix would have given us — focus capture on open, focus restore on close,
// Escape to dismiss, body scroll lock, and aria-modal wiring.

import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export const SidePanel: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Sticky footer content, e.g. a close button or a drill-down link. */
  footer?: React.ReactNode;
  width?: 'md' | 'lg';
  children: React.ReactNode;
}> = ({ open, onClose, title, subtitle, footer, width = 'md', children }) => {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);
  const titleId = React.useId();

  // Remember what was focused before we opened so we can hand focus back.
  React.useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => restoreFocusRef.current?.focus?.();
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-1300 flex justify-end'>
      <div className='absolute inset-0 bg-black/40' onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-border bg-card shadow-xl outline-none',
          width === 'lg' ? 'max-w-2xl' : 'max-w-lg',
        )}
      >
        <div className='flex items-start justify-between gap-4 border-b border-border p-5'>
          <div className='min-w-0'>
            <h2 id={titleId} className='text-base font-semibold'>
              {title}
            </h2>
            {subtitle && <p className='mt-0.5 text-sm text-muted-foreground'>{subtitle}</p>}
          </div>
          <Button variant='ghost' size='icon' onClick={onClose} aria-label='Close panel' className='shrink-0'>
            <X className='size-4' aria-hidden />
          </Button>
        </div>

        <div className='flex-1 overflow-y-auto p-5'>{children}</div>

        {footer && <div className='flex items-center justify-end gap-2 border-t border-border p-4'>{footer}</div>}
      </div>
    </div>
  );
};

/** Titled block inside a panel — keeps the panels visually consistent. */
export const PanelSection: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className='flex flex-col gap-2 border-b border-border py-4 first:pt-0 last:border-b-0'>
    <h3 className='text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{title}</h3>
    {children}
  </section>
);
